import {
    ChangeDetectorRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    Directive,
    Injector,
} from '@angular/core';
import {
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiFilterType, FilterTypeLabelSet } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { FiItemsListFilterHelperService } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';
import { groupBy } from '@fi-shared/utils/group-by';
import { SaveChangesWarningService } from '../fi-shared-services/save-changes-warning.service';
import { AppTextService } from '../app-text';
import { isEqual } from '../utils/is-equal';
import { ItemsListFilterDialogSnackbarService } from '../fi-items-list/fi-items-list-filter/services/items-list-filter-dialog-snackbar.service';

export interface IReportFilter {
    Guid?: string;
    Name?: string;
    [key: string]: any;
}

export interface IReportAvailableItems {
    [key: string]: any;
}

// todo: implement signal-based version of this component

@Directive()
export abstract class FiReportTableFilterBaseComponent<
    TFilter extends IReportFilter,
    TAvailableItems extends IReportAvailableItems,
>
    implements OnInit, OnChanges
{
    @Input({ required: true }) currentFilter: TFilter;
    @Input() isFilterChanged: boolean = false;
    @Output() currentFilterChange = new EventEmitter<TFilter>();
    @Output() selectedItemsChange = new EventEmitter<void>();

    @Input() savedFilters: TFilter[] = [];
    @Output() savedFiltersChange = new EventEmitter<TFilter[]>();
    @Output() onSelectFilter = new EventEmitter<string>();
    @Output() onUpdateCurrentFilter = new EventEmitter<void>();
    @Output() onResetCurrentFilter = new EventEmitter<void>();

    // Note: availableItems should be defined by derived classes as either @Input or getter
    // This is a placeholder that should be overridden
    protected availableItems: TAvailableItems = {} as TAvailableItems;

    protected menusData: ISharingMenuData<INameIdObj>[] = [];
    protected selectedFilterItems: ISelectedFilterItem[] = [];
    protected selectedFilterItemsGroups: { [key: number]: ISelectedFilterItem[] } = {};

    protected objectKeys = Object.keys;
    protected readonly FilterTypeLabelSet = FilterTypeLabelSet;
    constructor(private injector: Injector) {}

    protected itemsListFilterHelperService = this.injector.get(FiItemsListFilterHelperService);
    protected cdr = this.injector.get(ChangeDetectorRef);
    protected saveChangesWarningService = this.injector.get(SaveChangesWarningService);
    protected appTextService = this.injector.get(AppTextService);
    protected itemsListFilterDialogSnackbarService = this.injector.get(ItemsListFilterDialogSnackbarService);

    public ngOnInit(): void {
        this.initMenusData();
        this.setSelectedFilterItems();
        this.setSelectedFilterItemsGroups();
        this.setSelectedMenuItems();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const currentFilterChanged = changes.currentFilter && !changes.currentFilter.firstChange;
        if (currentFilterChanged) {
            this.setSelectedFilterItems();
            this.setSelectedFilterItemsGroups();
            this.setSelectedMenuItems();
        }
    }

    protected resetCurrentFilter(): void {
        this.onResetCurrentFilter.emit();
    }

    protected onSelectedItemRemoved(selectedItem: ISelectedFilterItem): void {
        this.selectedFilterItems = this.selectedFilterItems.filter((item) => !isEqual(item, selectedItem));
        this.setSelectedFilterItemsGroups();
        this.setSelectedMenuItems();
        this.setCurrentFilterBySelectedItems();
    }

    protected sharingWithItemChange(itemFilterType: FiFilterType, selectedItems: number[]): void {
        const filterPropertyName: string = this.itemsListFilterHelperService.filterTypeToPropertyNameRecord[itemFilterType];

        if (!selectedItems.length) {
            delete (this.currentFilter as any)[filterPropertyName];
        } else {
            (this.currentFilter as any)[filterPropertyName] = selectedItems;
        }
        this.setSelectedFilterItems();
        this.setSelectedFilterItemsGroups();
        this.currentFilterChange.emit(this.currentFilter);
    }

    protected setSelectedFilterItemsGroups(): void {
        this.selectedFilterItemsGroups = groupBy(this.selectedFilterItems, 'filterType');
    }

    protected setCurrentFilterBySelectedItems(): void {
        this.currentFilter = this.getFilterFromSelectedItems(this.selectedFilterItemsGroups, this.currentFilter);
        this.currentFilterChange.emit(this.currentFilter);
        this.selectedItemsChange.emit();
    }

    protected abstract initMenusData(): void;

    protected abstract setSelectedFilterItems(): void;

    protected abstract setSelectedMenuItems(): void;

    protected abstract selectFilter(filterGuid: string): void;

    protected abstract getSelectedFilterItemsFromFilter(filter: TFilter): ISelectedFilterItem[];

    protected getFilterFromSelectedItems(
        selectedFilterItemsGroups: { [key: number]: ISelectedFilterItem[] },
        currentFilter: TFilter,
    ): TFilter {
        // Default implementation - override in derived classes
        return currentFilter;
    }

    isMenuAvailable(menuDataItem: ISharingMenuData<INameIdObj>): boolean {
        return !menuDataItem.isAvailable || menuDataItem.isAvailable();
    }

    protected openChangeSavedFilterDialog(savedFilter: TFilter) {
        this.setSelectedFilterItems();
        const savedFilterItems: ISelectedFilterItem[] = this.getSelectedFilterItemsFromFilter(savedFilter);
        this.itemsListFilterDialogSnackbarService
            .getEditFilterDialog(savedFilter.Name, savedFilterItems)
            .onYesButtonClick((result: ISelectedFilterItem[]) => {
                if (!result.length) {
                    this.showNoFilterItemsWarningDialog(savedFilter);
                } else {
                    this.changeSavedFilter(savedFilterItems, savedFilter, result);
                }
            });
    }

    private changeSavedFilter(
        savedFilterItems: ISelectedFilterItem[],
        savedFilter: TFilter,
        newSelectedFilterItems: ISelectedFilterItem[],
    ) {
        savedFilterItems
            .filter((savedFilterItem: ISelectedFilterItem) => newSelectedFilterItems.indexOf(savedFilterItem) === -1)
            .forEach((removedItem: ISelectedFilterItem) => {
                const filterPropertyName: string =
                    this.itemsListFilterHelperService.filterTypeToPropertyNameRecord[removedItem.filterType];
                savedFilter[filterPropertyName].splice(savedFilter[filterPropertyName].indexOf(removedItem.value), 1);
            });
        this.savedFiltersChange.emit(this.savedFilters);
        this.itemsListFilterDialogSnackbarService.showFilterUpdatedSnackbar(savedFilter.Name);
    }

    private showNoFilterItemsWarningDialog(filter: TFilter) {
        this.itemsListFilterDialogSnackbarService
            .getNoFilterItemsWarningDialog()
            .onYesButtonClick(() => this.removeFilter(filter));
    }

    protected openRenameSavedFilterDialog(savedFilter: TFilter) {
        this.itemsListFilterDialogSnackbarService
            .getSaveFilterDialog(
                savedFilter.Name,
                this.appTextService.get('filter.rename_filter'),
                'fal fa-pen-to-square BrandedColor',
                this.appTextService.get('filter.update_description'),
            )
            .onYesButtonClick((newFilterName: string) => {
                savedFilter.Name = newFilterName;
                this.savedFiltersChange.emit(this.savedFilters);
                this.cdr.markForCheck();
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    protected openDeleteSavedFilterDialog(savedFilter: TFilter) {
        this.itemsListFilterDialogSnackbarService
            .getConfirmDeleteSavedFilterDialog()
            .onYesButtonClick(() => this.removeFilter(savedFilter));
    }

    private removeFilter(savedFilter: TFilter) {
        const savedFilterIndex: number = this.savedFilters.indexOf(savedFilter);
        this.savedFilters.splice(savedFilterIndex, 1);
        this.savedFiltersChange.emit(this.savedFilters);
    }
}
